/**
 * PrimasAdmin - Admin page for managing bonuses/primas
 */
import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Loader2, Save, Award, Globe, Check } from 'lucide-react';
import { primas as primasApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import ConfirmModal from '../components/ConfirmModal';

export default function PrimasAdmin() {
    const { success, error: toastError } = useToast();
    const [primas, setPrimas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, prima: null });

    const [form, setForm] = useState({
        codigo: '',
        nombre: '',
        descripcion: '',
        importe: '',
        es_global: false
    });

    useEffect(() => {
        loadPrimas();
    }, []);

    const loadPrimas = async () => {
        try {
            setLoading(true);
            const data = await primasApi.getAllAdmin();
            setPrimas(data);
        } catch (err) {
            toastError('Error al cargar primas');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (prima = null) => {
        if (prima) {
            setEditing(prima);
            setForm({
                codigo: prima.codigo,
                nombre: prima.nombre,
                descripcion: prima.descripcion || '',
                importe: prima.importe?.toString() || '',
                es_global: prima.es_global
            });
        } else {
            setEditing(null);
            setForm({ codigo: '', nombre: '', descripcion: '', importe: '', es_global: false });
        }
        setShowModal(true);
    };

    const savePrima = async () => {
        if (!form.codigo || !form.nombre) {
            toastError('Codigo y nombre son requeridos');
            return;
        }
        try {
            setSaving(true);
            const data = {
                ...form,
                importe: parseFloat(form.importe) || 0
            };
            if (editing) {
                await primasApi.update(editing.id, data);
                success('Prima actualizada');
            } else {
                await primasApi.create(data);
                success('Prima creada');
            }
            setShowModal(false);
            loadPrimas();
        } catch (err) {
            toastError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const openDeletePrima = (prima) => {
        setDeleteConfirm({ isOpen: true, prima });
    };

    const confirmDeletePrima = async () => {
        const primaId = deleteConfirm.prima?.id;
        if (!primaId) return;
        try {
            await primasApi.delete(primaId);
            success('Prima eliminada');
            loadPrimas();
        } catch (err) {
            toastError(err.message);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Award className="w-6 h-6 text-green-400" />
                        Primas y Bonificaciones
                    </h1>
                    <p className="text-gray-400 text-sm mt-1">
                        Gestiona las primas para el calculo de nomina
                    </p>
                </div>
                <button
                    onClick={() => openModal()}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-green-500 text-gray-900 font-semibold rounded-lg hover:shadow-lg transition-all"
                >
                    <Plus className="w-4 h-4" />
                    Nueva Prima
                </button>
            </div>

            {/* Info box */}
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <p className="text-yellow-400 text-sm flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <strong>Primas globales</strong> aplican a TODOS los servidores automaticamente (ej: Festivo)
                </p>
            </div>

            {/* Primas table */}
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-white/10">
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Codigo</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nombre</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Importe</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Global</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Estado</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {primas.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                                    No hay primas registradas
                                </td>
                            </tr>
                        ) : (
                            primas.map(prima => (
                                <tr key={prima.id} className="hover:bg-white/5">
                                    <td className="px-4 py-3">
                                        <span className="font-mono text-cyan-400 font-bold">{prima.codigo}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-white">{prima.nombre}</span>
                                        {prima.descripcion && (
                                            <p className="text-xs text-gray-500 mt-0.5">{prima.descripcion}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-green-400 font-medium">{prima.importe} €</span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {prima.es_global ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                                                <Globe className="w-3 h-3" /> Si
                                            </span>
                                        ) : (
                                            <span className="text-gray-500 text-xs">No</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {prima.activa ? (
                                            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                                                Activa
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                                                Inactiva
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() => openModal(prima)}
                                                className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-cyan-400"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => openDeletePrima(prima)}
                                                className="p-2 hover:bg-red-500/20 rounded-lg text-gray-400 hover:text-red-400"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-cyan-500/20 w-full max-w-md p-6">
                        <h2 className="text-xl font-bold text-white mb-4">
                            {editing ? 'Editar Prima' : 'Nueva Prima'}
                        </h2>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Codigo</label>
                                    <input
                                        type="text"
                                        value={form.codigo}
                                        onChange={(e) => setForm(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                                        className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cyan-500"
                                        placeholder="NOCT"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Importe (€)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={form.importe}
                                        onChange={(e) => setForm(prev => ({ ...prev, importe: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cyan-500"
                                        placeholder="15.00"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Nombre</label>
                                <input
                                    type="text"
                                    value={form.nombre}
                                    onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cyan-500"
                                    placeholder="Nocturnidad"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Descripcion (opcional)</label>
                                <textarea
                                    value={form.descripcion}
                                    onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cyan-500 resize-none"
                                    rows={2}
                                />
                            </div>
                            <button
                                onClick={() => setForm(prev => ({ ...prev, es_global: !prev.es_global }))}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${form.es_global
                                        ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                                        : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                                    }`}
                            >
                                <Globe className="w-5 h-5" />
                                <span className="flex-1 text-left">Prima Global (aplica a todos)</span>
                                {form.es_global && <Check className="w-5 h-5" />}
                            </button>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowModal(false)}
                                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={savePrima}
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-green-500 text-gray-900 font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, prima: null })}
                onConfirm={confirmDeletePrima}
                title="Eliminar prima"
                message={`¿Eliminar la prima "${deleteConfirm.prima?.nombre || ''}"?`}
                confirmText="Eliminar"
                variant="danger"
            />
        </div>
    );
}
